import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Divider,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd";
import { ApiOutlined, KeyOutlined } from "@ant-design/icons";
import { AuthContext } from "../../context/auth-context/AuthContext";
import apiService from "../../services/apiServices";

const { Paragraph, Text, Title } = Typography;

const MCP_URL = `${process.env.REACT_APP_BASE_URL}/mcp`;
const TOKEN_DE_EJEMPLO = "inm_TU_TOKEN";

const HERRAMIENTAS = [
  ["listar_experiencias", "tus experiencias, con cantidad de escenas y link público"],
  ["ver_experiencia", "escenas, elementos, link de carrito y enlaces de una experiencia"],
  ["ver_metricas", "visitas, carrito, tiempo promedio, dispositivos y ciudades"],
  ["link_publico", "el link y el código iframe para insertar una experiencia en tu web"],
];

const fecha = (valor) =>
  valor
    ? new Date(valor).toLocaleString("es-AR", { dateStyle: "short", timeStyle: "short", hour12: false })
    : "—";

const Codigo = ({ children }) => (
  <Paragraph
    copyable={{ text: children, tooltips: ["Copiar", "Copiado"] }}
    style={{
      margin: 0,
      padding: "10px 12px",
      borderRadius: 8,
      background: "rgba(0,0,0,0.35)",
      border: "1px solid #303030",
      fontFamily: "Consolas, Menlo, monospace",
      fontSize: 12.5,
      whiteSpace: "pre-wrap",
      wordBreak: "break-all",
    }}
  >
    {children}
  </Paragraph>
);

export const Integraciones = ({ visible, alCerrar, comoPagina = false }) => {
  const abierta = comoPagina || visible;
  const { handleRefreshToken } = useContext(AuthContext);
  const [tokens, setTokens] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [noDisponible, setNoDisponible] = useState(false);
  const [error, setError] = useState(null);
  const [nombre, setNombre] = useState("");
  const [creando, setCreando] = useState(false);
  const [nuevo, setNuevo] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const jwt = await handleRefreshToken();
      setTokens(await apiService.listarTokensIntegracion(jwt));
      setNoDisponible(false);
    } catch (e) {
      //404: la API todavia no tiene las integraciones desplegadas
      if (e.status === 404) setNoDisponible(true);
      else setError("No pudimos cargar tus tokens. Intentá de nuevo en unos minutos.");
    } finally {
      setCargando(false);
    }
  }, [handleRefreshToken]);

  useEffect(() => {
    if (abierta) cargar();
    else setNuevo(null); //el token completo se muestra solo mientras la ventana sigue abierta
  }, [abierta, cargar]);

  const crear = async () => {
    if (!nombre.trim()) return;
    setCreando(true);
    setError(null);
    try {
      const jwt = await handleRefreshToken();
      const creado = await apiService.crearTokenIntegracion(jwt, nombre.trim());
      setNuevo(creado);
      setNombre("");
      await cargar();
    } catch (e) {
      setError(e.message || "No se pudo crear el token.");
    } finally {
      setCreando(false);
    }
  };

  const revocar = async (id) => {
    try {
      const jwt = await handleRefreshToken();
      await apiService.revocarTokenIntegracion(jwt, id);
      if (nuevo?.id === id) setNuevo(null);
      await cargar();
    } catch (e) {
      setError("No se pudo revocar el token.");
    }
  };

  const token = nuevo?.token || TOKEN_DE_EJEMPLO;
  const activos = tokens.filter((t) => !t.revocado).length;

  const columnas = [
    { title: "Nombre", dataIndex: "nombre", key: "nombre" },
    { title: "Token", dataIndex: "prefijo", key: "prefijo", render: (p) => <Text code>{p}…</Text> },
    { title: "Creado", dataIndex: "creado", key: "creado", render: fecha },
    { title: "Último uso", dataIndex: "ultimoUso", key: "ultimoUso", render: fecha },
    {
      title: "Estado",
      key: "estado",
      render: (_, t) => (t.revocado ? <Tag>Revocado</Tag> : <Tag color="green">Activo</Tag>),
    },
    {
      title: "",
      key: "accion",
      align: "right",
      render: (_, t) =>
        t.revocado ? null : (
          <Popconfirm
            title="Revocar token"
            description="El asistente que lo use pierde el acceso en ese momento."
            okText="Revocar"
            cancelText="Cancelar"
            okButtonProps={{ danger: true }}
            onConfirm={() => revocar(t.id)}
          >
            <Button size="small" danger>
              Revocar
            </Button>
          </Popconfirm>
        ),
    },
  ];

  const contenido = (
    <>
      <Paragraph type="secondary" style={{ marginTop: 4 }}>
        Conectá un asistente de IA, como Claude o un agente propio, a tus experiencias de Inmersial. Podés
        preguntarle por tus recorridos, sus visitas o pedirle el link para compartir uno. Por ahora el acceso es de{" "}
        <b>solo lectura</b>: el asistente no puede modificar ni borrar nada.
      </Paragraph>

      {noDisponible && (
        <Alert
          type="info"
          showIcon
          message="Las integraciones todavía no están activas"
          description="Estarán disponibles cuando se actualice el servidor. Mientras tanto podés leer cómo funcionan."
          style={{ marginBottom: 16 }}
        />
      )}
      {error && <Alert type="error" showIcon message={error} closable onClose={() => setError(null)} style={{ marginBottom: 16 }} />}

      <Title level={5} style={{ marginTop: 8 }}>
        1. Creá un token
      </Title>
      <Space.Compact style={{ width: "100%", maxWidth: 520 }}>
        <Input
          prefix={<KeyOutlined />}
          placeholder="Nombre, por ejemplo: Claude de Juan"
          maxLength={80}
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          onPressEnter={crear}
          disabled={noDisponible}
        />
        <Button type="primary" onClick={crear} loading={creando} disabled={!nombre.trim() || noDisponible}>
          Crear token
        </Button>
      </Space.Compact>

      {nuevo && (
        <Alert
          type="success"
          showIcon
          style={{ marginTop: 12 }}
          message={`Token "${nuevo.nombre}" creado. Copialo ahora: no se vuelve a mostrar.`}
          description={<Codigo>{nuevo.token}</Codigo>}
        />
      )}

      {!noDisponible && (
        <Table
          style={{ marginTop: 16 }}
          size="small"
          rowKey="id"
          loading={cargando}
          columns={columnas}
          dataSource={tokens}
          pagination={false}
          locale={{ emptyText: "Todavía no creaste tokens." }}
          footer={() => <Text type="secondary">{activos} de 10 tokens activos</Text>}
        />
      )}

      <Divider />
      <Title level={5}>2. Conectá tu asistente</Title>
      <Paragraph type="secondary">
        Dirección del servidor MCP: <Text code copyable>{MCP_URL}</Text>
        {!nuevo && (
          <>
            <br />
            En los ejemplos, reemplazá <Text code>{TOKEN_DE_EJEMPLO}</Text> por tu token.
          </>
        )}
      </Paragraph>
      <Tabs
        items={[
          {
            key: "claude-code",
            label: "Claude Code",
            children: (
              <>
                <Paragraph>Ejecutá este comando en la terminal:</Paragraph>
                <Codigo>{`claude mcp add --transport http inmersial ${MCP_URL} --header "Authorization: Bearer ${token}"`}</Codigo>
              </>
            ),
          },
          {
            key: "claude-desktop",
            label: "Claude Desktop",
            children: (
              <>
                <Paragraph>
                  En Claude Desktop abrí <Text strong>Configuración → Desarrollador → Editar configuración</Text> y
                  agregá esto en <Text code>claude_desktop_config.json</Text>. Requiere Node.js instalado.
                </Paragraph>
                <Codigo>
                  {JSON.stringify(
                    {
                      mcpServers: {
                        inmersial: {
                          command: "npx",
                          args: ["mcp-remote", MCP_URL, "--header", `Authorization: Bearer ${token}`],
                        },
                      },
                    },
                    null,
                    2
                  )}
                </Codigo>
              </>
            ),
          },
          {
            key: "otro",
            label: "Otro cliente",
            children: (
              <>
                <Paragraph>
                  Cualquier cliente compatible con MCP por HTTP (Streamable HTTP) se conecta con estos datos:
                </Paragraph>
                <Codigo>{`URL: ${MCP_URL}\nHeader: Authorization: Bearer ${token}`}</Codigo>
                <Paragraph type="secondary" style={{ marginTop: 10, marginBottom: 0 }}>
                  Los conectores que se agregan desde la web de Claude.ai o ChatGPT todavía no son compatibles: piden
                  otro tipo de autenticación (OAuth) que se sumará más adelante.
                </Paragraph>
              </>
            ),
          },
        ]}
      />

      <Divider />
      <Title level={5}>Qué puede consultar el asistente</Title>
      <ul style={{ paddingLeft: 18, margin: 0 }}>
        {HERRAMIENTAS.map(([nombreHerramienta, descripcion]) => (
          <li key={nombreHerramienta} style={{ marginBottom: 4 }}>
            <Text code>{nombreHerramienta}</Text> {descripcion}
          </li>
        ))}
      </ul>
      <Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0 }}>
        Cada token ve solo tus experiencias. Si dejás de usar un asistente, revocá su token.
      </Paragraph>
    </>
  );

  if (comoPagina) {
    return (
      <div className="inm-pagina">
        <header className="inm-pagina__cabecera">
          <h1 className="inm-pagina__titulo">Integraciones</h1>
        </header>
        <div className="inm-caja" style={{ maxWidth: 900 }}>
          {contenido}
        </div>
      </div>
    );
  }

  return (
    <Modal
      open={visible}
      onCancel={alCerrar}
      footer={null}
      width={820}
      title={
        <Space>
          <ApiOutlined /> Integraciones con IA (MCP)
        </Space>
      }
    >
      {contenido}
    </Modal>
  );
};
